// Turns an HTTP failure into one short Chinese sentence for the page.
// Backend messages that are already Chinese win; anything else (English,
// masked 500s, HTML error pages, raw JSON) falls back to a status message.
const CJK = /[一-鿿]/;

export const NETWORK_ERROR_MESSAGE = '无法连接服务器，请检查网络后重试';

export function statusErrorMessage(status: number): string {
    if (status === 400 || status === 422) return '提交的内容有误，请检查后重试';
    if (status === 401) return '登录已过期，请重新登录';
    if (status === 403) return '没有权限执行此操作';
    if (status === 404) return '内容不存在或已删除';
    if (status === 409) return '数据已变更，请刷新后重试';
    if (status === 405) return '此功能尚未启用，请联系管理员';
    if (status === 408 || status === 504) return '服务器响应超时，请重试';
    if (status === 415) return '文件格式不受支持，请更换文件';
    if (status === 502 || status === 503) return '服务暂时不可用，请稍后重试';
    if (status === 413) return '文件太大，请压缩后重试';
    if (status === 429) return '操作太频繁，请稍后再试';
    if (status >= 500) return '服务器出错，请稍后重试';
    return '未收到有效的服务器响应，请重新加载页面';
}

export function publicErrorText(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const text = value.trim();
    // A plain-text body may be an HTML error page; never show markup.
    if (!text || text.length > 400 || !CJK.test(text)) return null;
    if (text === '不支持该操作' || text === '当前页面不支持此操作，请刷新页面') return null;
    if (/(Traceback|Exception|TypeError|ReferenceError|SyntaxError|\bSQL\b|ECONN|ENOTFOUND|localhost|request[_ -]?id|req_[a-z0-9]+|stack|undefined|\bnull\b|\/api\/|https?:\/\/|<[^>]+>|\{\s*")/i.test(text)) return null;
    if (/^(?:批量)?操作(?:失败|未完成)|^未知错误|^发生(?:未知)?错误|^出了点问题|^系统内部错误/.test(text)) return null;
    return text;
}

export function apiErrorMessage(status: number, body: unknown): string {
    const data = body && typeof body === 'object' ? (body as Record<string, any>) : null;
    const message = (data
        ? publicErrorText(data.error) ?? publicErrorText(data.error?.message) ?? publicErrorText(data.message)
        : publicErrorText(body)) ?? statusErrorMessage(status);
    return message;
}

export async function readErrorBody(res: Response): Promise<unknown> {
    const text = await res.text().catch(() => '');
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

// For errors caught in components: keep a clean Chinese message, else the caller's fallback.
export function chineseErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.name === 'AbortError') return '';
    const value = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    if (/Failed to fetch|NetworkError|Network request failed|Network Error|ECONN|ENOTFOUND/i.test(value)) return NETWORK_ERROR_MESSAGE;
    if (/timeout|timed out/i.test(value) || (error instanceof Error && error.name === 'TimeoutError')) return '服务器响应超时，请重试';
    if (!value) return publicErrorText(fallback) ?? '';
    let parsed: unknown = null;
    try {
        parsed = JSON.parse(value);
    } catch {
        // Not JSON — a plain message.
    }
    if (parsed && typeof parsed === 'object') {
        // Older call sites threw the raw response body.
        const message = apiErrorMessage(0, parsed);
        return message === statusErrorMessage(0) ? publicErrorText(fallback) ?? '' : message;
    }
    return publicErrorText(value) ?? publicErrorText(fallback) ?? '';
}
